<template>
   <Head>
      <title>{{ $t("meta.login_code.title") }}</title>
      <meta name="description" :content="$t('meta.login_code.content')" />
   </Head>

   <AuthLayout :heading="$t('authentication.enter_login_code')">
      <template v-slot:description>
         <p>
            {{ $t("authentication.enter_login_code_hint") }}
         </p>
      </template>
      <template v-slot:form>
         <!-- login code input --->
         <div class="flex h-full flex-col justify-between text-center">
            <div class="relative w-full">
               <form @submit.prevent>
                  <LoginCodeInput
                     :showValidation="state.showValidation"
                     class="mb-4"
                     @login-code-entered="setLoginCode"
                     @login-code-invalidated="resetLoginCode"
                  />

                  <LoadingButton
                     class="mx-auto mt-8 w-full"
                     :text="$t('authentication.confirm_login_code')"
                     :isLoading="state.isLoading"
                     data-cy="submit-code-btn"
                     @click="attemptSubmission"
                  />
               </form>
            </div>
         </div>
      </template>
   </AuthLayout>
</template>

<script setup lang="ts">
import { inject } from "vue";
import { Head } from "@unhead/vue/components";
import LoginCodeInput from "@/components/auth/LoginCodeInput.vue";
import { useRouter, useRoute } from "vue-router";
import { useToastKey } from "@/plugins/useToast";
import { useI18n } from "@/vue-i18n/vue-i18n";
import UsersApi from "@/data/api-layer/usersApi";
import { addLocaleToRedirectPath } from "@/router/routes-config";

const $router = useRouter();
const $route = useRoute();
const useToast = inject(useToastKey);
const i18n = useI18n();

// data & requests
// -----------------------------------------
const state = reactive({
   isLoading: false,
   loginCode: "",
   showValidation: false,
   // max login attempts before user is redirected to the login page is 3
   loginAttemptCount: 0,
});

// computed
// -----------------------------------------
const isDisabled = computed(() => {
   return state.loginCode.length !== 6;
});

// methods
// -----------------------------------------
const setLoginCode = (val) => {
   // login code has been validated and set from child
   state.loginCode = val;
};

const resetLoginCode = () => {
   state.showValidation = false;
   state.loginCode = "";
   state.isLoading = false;
};

const attemptSubmission = async () => {
   state.isLoading = true;

   // because we want to hide validation until submission.
   if (isDisabled.value) {
      state.isLoading = false;
      state.showValidation = true;
   } else {
      UsersApi.submitLoginCode(state.loginCode)
         .then((response) => {
            interface QueryParams {
               redirect?: string; // ex /account
               params?: any; // any other parameters in the query string
            }

            state.isLoading = true;

            // Redirect, keeping other params intact
            const { redirect, ...params }: QueryParams = $route.query;

            // if the user was directed to a flow (likely from a logged out study page), redirect them there
            if (redirect) {
               $router.push({ path: addLocaleToRedirectPath(redirect, i18n), query: params });
            }
            // or redirect if someone was trying to access a particular page before being sent to login (home is default)
            else {
               $router.push({ name: "home", query: params, params: { locale: $route.params?.locale } });
            }
         })
         .catch((error) => {
            resetLoginCode();
            // TODO destroy token here and force logout?
            if (error.response?.data?.error === "Incorrect code entered.") {
               useToast({ title: i18n.t("validation.login_code.incorrect"), type: "error" });
            }

            // TODO: We SHOULD be getting an error from the BE about reaching the max tries and timeout access to this page for 15 minutes
            // (waiting for BE to implement this)
            state.loginAttemptCount += 1;
            if (state.loginAttemptCount >= 3) {
               $router.push({ name: "login", params: { locale: $route.params?.locale } });
            }
         });
   }
};
</script>
